import * as React from 'react';
import { cn } from '../../utils/cn';

type TextElement = 'p' | 'span' | 'div' | 'label';
type TextSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl';
type TextWeight = 'normal' | 'medium' | 'semibold' | 'bold';
type TextColor = 'default' | 'primary' | 'secondary' | 'muted' | 'error' | 'success';
type TextAlign = 'left' | 'center' | 'right' | 'justify';
type TextVariant = 'body' | 'caption' | 'overline' | 'helper' | 'error';

export interface TextProps {
  as?: TextElement;
  size?: TextSize;
  weight?: TextWeight;
  color?: TextColor;
  align?: TextAlign;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  uppercase?: boolean;
  lowercase?: boolean;
  capitalize?: boolean;
  truncate?: boolean;
  clamp?: number;
  nowrap?: boolean;
  variant?: TextVariant;
  children: React.ReactNode;
  className?: string;
}

const sizeClasses: Record<TextSize, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
};

const weightClasses: Record<TextWeight, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

const colorClasses: Record<TextColor, string> = {
  default: 'text-base-content',
  primary: 'text-primary',
  secondary: 'text-secondary',
  muted: 'text-base-content/70',
  error: 'text-error',
  success: 'text-success',
};

const alignClasses: Record<TextAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
};

const variantPresets: Record<TextVariant, Partial<TextProps>> = {
  body: { size: 'base', weight: 'normal' },
  caption: { size: 'sm', color: 'muted' },
  overline: { size: 'xs', weight: 'semibold', uppercase: true },
  helper: { size: 'sm', color: 'muted', italic: true },
  error: { size: 'sm', color: 'error', weight: 'medium' },
};

export const Text = React.forwardRef<HTMLElement, TextProps>(
  (
    {
      as: Component = 'p',
      size,
      weight,
      color,
      align = 'left',
      italic = false,
      underline = false,
      strikethrough = false,
      uppercase = false,
      lowercase = false,
      capitalize = false,
      truncate = false,
      clamp,
      nowrap = false,
      variant,
      children,
      className,
    },
    ref
  ) => {
    // Apply variant presets if specified
    const variantProps = variant ? variantPresets[variant] : {};
    const finalSize = size || variantProps.size || 'base';
    const finalWeight = weight || variantProps.weight || 'normal';
    const finalColor = color || variantProps.color || 'default';
    const finalItalic = italic || variantProps.italic || false;
    const finalUppercase = uppercase || variantProps.uppercase || false;

    const textTransformClass =
      uppercase || finalUppercase
        ? 'uppercase'
        : lowercase
          ? 'lowercase'
          : capitalize
            ? 'capitalize'
            : '';

    const decorationClass = underline ? 'underline' : strikethrough ? 'line-through' : '';

    const clampClass = clamp ? `line-clamp-${clamp}` : truncate ? 'truncate' : '';

    return (
      <Component
        ref={ref as any}
        className={cn(
          sizeClasses[finalSize],
          weightClasses[finalWeight],
          colorClasses[finalColor],
          alignClasses[align],
          finalItalic && 'italic',
          decorationClass,
          textTransformClass,
          clampClass,
          nowrap && 'whitespace-nowrap',
          'leading-relaxed',
          className
        )}
      >
        {children}
      </Component>
    );
  }
);

Text.displayName = 'Text';
